import type { CdtStudioTranslations } from "../en/CdtStudio";
import type { TranslationShape } from "../../store/types";

export const cdtStudioEs: TranslationShape<CdtStudioTranslations> = {
  pageTitle: "CDT Studio",
  loading: "Cargando planograma base...",
  loadFailed: "No se pudo cargar el planograma base.",
  inProgress: "El planograma base todavía se está generando. Inténtalo de nuevo en un momento.",
  itemsSummary: "{{count}} SKUs · {{bays}} módulos",
  placementRule: {
    chip: "Regla de ubicación",
    where: "Observado en los estantes {{shelves}} · bahías {{bays}}",
    title: "¿Mantener {{variant}} en los estantes {{shelves}}?",
    detail:
      "La agrupación seleccionada puede colocar algunos de estos productos en otros estantes al " +
      "regenerar el diseño. Guardar esta regla los mantiene en los estantes indicados, formando " +
      "un bloque limpio siempre que el mueble lo permita.",
    detailTreeHolds:
      "Guardar esta regla mantiene estos productos en los estantes indicados al generar los " +
      "planogramas, formando un bloque limpio siempre que el mueble lo permita.",
    cost:
      "Abarca {{cells}} ubicación(es) de estante. El ancho y el reparto de frentes siguen siendo " +
      "flexibles, sujetos a otras restricciones. Detalle completo en el recibo de finalización.",
    promotionNote:
      'Para aplicar esta regla por separado, "{{variant}}" pasa a ser su propio grupo de productos dentro de {{parent}}.',
    keep: "Usar esta regla",
    decline: "Dejar la ubicación flexible",
    // No "Deshacer": reabre la pregunta de ESTA regla. Deshacer es la barra.
    changeAnswer: "Cambiar respuesta",
    keptChip: "Seleccionada — se guarda al terminar",
    declinedChip: "Ubicación flexible",
    keptSummary: "{{count}} regla(s) de ubicación seleccionada(s) — se guardan al terminar",
  },
  setup: {
    title: "Configuración de CDT Studio",
    intro:
      "Define el perfil de nombres y las reglas de inferencia que usa CDT Studio. Los atributos de producto vienen de la API de categoría.",
    summary: "{{attributes}} atributos",
    summaryPlural: "{{attributes}} atributos",
    sections: {
      basic: "Perfil y columnas de origen",
      attributes: "Mapeo y orden de atributos",
      semantic: "Roles semánticos e inferencia",
      normalization: "Normalización de cantidades",
      hierarchy: "Movimientos de jerarquía",
      vertical: "Bloqueo vertical",
      aesthetics: "Estética",
      advanced: "Editor avanzado del perfil",
    },
    actions: {
      close: "Cerrar",
      reset: "Restablecer",
      save: "Guardar configuración",
      addAttribute: "Añadir atributo",
      addTier: "Añadir nivel",
      remove: "Eliminar",
      moveUp: "Subir",
      moveDown: "Bajar",
    },
    fields: {
      merchant: "Comercio",
      category: "Categoría",
      profileVersion: "Versión del perfil",
      itemIdentifier: "Identificador de artículo del POG",
      skuColumn: "Columna SKU",
      nameColumn: "Columna de nombre",
      attributeKey: "Clave",
      attributeLabel: "Etiqueta",
      sourceColumn: "Columna de origen",
      selectColumn: "Seleccionar columna CSV",
      pogField: "Campo del POG",
      notMapped: "Sin asignar — el usuario completa",
      missingValues: "Valores ausentes",
      blockIdentity: "Orden de identidad del bloque",
      verticalRoles: "Orden de roles verticales",
      brandRole: "Rol de marca",
      tierRole: "Rol de nivel",
      quantityRole: "Rol de cantidad",
      terminalVariantRole: "Rol de variante terminal",
      useFirstRegex: "Usar regex de primer valor",
      firstRegex: "Regex de primer valor",
      useLastRegex: "Usar regex de último valor",
      lastRegex: "Regex de último valor",
      normalizationEnabled: "Activar normalización de cantidades",
      inferFromName: "Inferir la cantidad del nombre del producto",
      zeroIsMissing: "Tratar cero como valor ausente",
      liquidOuncesAreFluid: "Tratar onzas líquidas como onzas fluidas",
      unitPreference: "Orden de preferencia de unidades",
      liquidNameTerms: "Términos líquidos en el nombre",
      minimumFamilyEvidence: "Evidencia mínima de familia",
      reconciliationMinimum: "Ratio mínimo de conciliación",
      reconciliationMaximum: "Ratio máximo de conciliación",
      minFacingsPerShelf: "Frentes mínimos por estante",
      roundTripFloorShare: "Proporción mínima de ida y vuelta",
      fillFloorShare: "Proporción mínima de ocupación",
      fillDropTolerancePts: "Tolerancia de caída de ocupación (puntos)",
      seamPass: "Pasada de alineación",
      blockShare: "Proporción de estantes del bloque",
      fragmentJoinCm: "Distancia de unión de fragmentos (cm)",
      minFillRatio: "Ratio mínimo de ocupación",
      preserveRange: "Conservar productos incluidos",
      onlyIfBetter: "Aplicar solo si mejora la puntuación",
      brandAttribute: "Atributo de marca",
      tierAttribute: "Atributo de nivel",
      sizeAttribute: "Atributo de cantidad",
      flavourAttribute: "Atributo de variante terminal",
      verticalAttributes: "Atributos verticales",
      tierLabel: "Etiqueta del nivel",
      tierValues: "Valores del nivel",
      lastInBlock: "Valores colocados al final del bloque",
      bayOwners: "Propietarios de módulo completo",
      bulkPounds: "Umbral de volumen (lb)",
      lowerShelfShare: "Proporción de estantes inferiores",
      jitterPacks: "Tolerancia del borde (paquetes)",
      raggedZeroPacks: "Irregularidad de puntuación cero (paquetes)",
      flagsPerHundredZero: "Alertas de puntuación cero por 100 SKUs",
      blockingPenalty: "Penalización de gravedad bloqueante",
      toCheckPenalty: "Penalización de gravedad por comprobar",
      toReviewPenalty: "Penalización de gravedad por revisar",
      blockingWeight: "Peso del bloqueo",
      cdtWeight: "Peso del CDT",
      fullnessWeight: "Peso de la ocupación",
      ergonomicsWeight: "Peso de la ergonomía",
      verticalSplit: "Reparto de bloqueo vertical",
      horizontalSplit: "Reparto de bloqueo horizontal",
      detectorSplit: "Reparto del detector CDT",
      orderingSplit: "Reparto del orden CDT",
      fullProfileJson: "JSON completo del perfil",
    },
    tooltips: {
      merchant:
        "Solo metadatos. Nombra el comercio propietario del perfil guardado de la categoría.",
      category: "Solo metadatos. Nombra la configuración de categoría; no ordena el CDT.",
      profileVersion: "Metadato que identifica esta revisión; no cambia la inferencia.",
      itemIdentifier:
        "Selecciona el campo del POG que se enlaza con el maestro. Una elección incorrecta deja productos ausentes o mal asociados antes de inferir.",
      skuColumn: "Columna CSV con el identificador usado para enlazar filas y productos.",
      nameColumn:
        "Columna CSV del nombre. Se muestra y puede aportar evidencia de cantidad si la inferencia por nombre está activa.",
      attributeKey:
        "Nombre estable usado por el motor en inferencia, roles, reglas y superposiciones.",
      attributeLabel: "Etiqueta visible del nivel jerárquico. No cambia la inferencia.",
      sourceColumn: "Columna CSV cuyos valores alimentan este atributo de inferencia.",
      pogField:
        "Campo opcional del POG para precargar filas faltantes del maestro. No cambia filas existentes.",
      missingValues:
        "Estas grafías, sin distinguir mayúsculas, se convierten en Unspecified y no crean ramas significativas.",
      blockIdentity:
        "Solo resolutor. Atributos de mayor a menor para auditar divisiones y huérfanos; no reordena la inferencia inicial.",
      verticalRoles:
        "Puntuación y reparación. Atributos ordenados que definen un bloque vertical; no eligen el árbol inicial.",
      brandRole:
        "Puntuación y diagnóstico. Atributo usado para agrupar marcas y detectar eliminaciones.",
      tierRole:
        "Puntuación y diagnóstico. Atributo cuyos niveles configurados se comprueban como bandas horizontales.",
      quantityRole:
        "Normalización y resolutor. Atributo tratado como cantidad/tamaño para familias y orden de tamaños.",
      terminalVariantRole:
        "Resolutor y puntuación. Atributo usado para el orden de variante terminal y valores finales.",
      useFirstRegex:
        "Activa la regex inicial guardada. Desactivarla no aplica ninguna regla implícita de Original o Regular.",
      firstRegex:
        "Desempate de inferencia. Solo adelanta coincidencias cuando el estante no aporta un orden.",
      useLastRegex:
        "Activa la regex final guardada. Desactivarla no aplica reglas implícitas de Sensitive o Free & Clear.",
      lastRegex:
        "Orden de valores. Lleva coincidencias al final; no elige el nivel del atributo en el árbol.",
      normalizationEnabled:
        "Activa análisis de cantidades, enriquecimiento por nombre y conciliación de familias para el rol de cantidad.",
      inferFromName:
        "Usa cantidades confirmadas del nombre para completar valores ausentes o números sin unidad.",
      zeroIsMissing: "Trata el cero numérico como ausente y permite recuperarlo desde el nombre.",
      liquidOuncesAreFluid:
        "Interpreta onzas ambiguas de líquidos como onzas fluidas y separa volumen de masa.",
      unitPreference:
        "Gana la primera unidad coincidente cuando el nombre contiene varias cantidades; no se añade un orden oculto.",
      liquidNameTerms:
        "Términos del nombre que identifican líquidos al decidir si las onzas son fluidas.",
      minimumFamilyEvidence:
        "Valores inequívocos necesarios para unir una cantidad ambigua a una familia. Un valor mayor es más conservador.",
      reconciliationMinimum:
        "Límite inferior inclusivo de proporción para unir valores ambiguos a una familia de volumen.",
      reconciliationMaximum:
        "Límite superior inclusivo de proporción para unir valores ambiguos a una familia de volumen.",
      minFacingsPerShelf:
        "Movimientos. Frentes por estante necesarios para tratar un valor movido como bloque vertical completo.",
      roundTripFloorShare:
        "Movimientos. Proporción mínima de productos reproducidos en el mismo estante tras regenerar la jerarquía.",
      fillFloorShare:
        "Movimientos. Ocupación mínima objetivo por lineal al cerrar huecos después de mover.",
      fillDropTolerancePts:
        "Movimientos. Puntos porcentuales de ocupación que se permite perder frente al estado anterior.",
      seamPass:
        "Reparación posterior: off la omite, facings-neutral la permite sin cambiar frentes y on permite la pasada completa.",
      blockShare:
        "Umbral del resolutor. Cobertura de estantes necesaria para considerar un valor bloque vertical; subirlo exige más cobertura.",
      fragmentJoinCm:
        "Inferencia directa. Distancia máxima aún tratada como un fragmento; subirla oculta más huecos pequeños.",
      minFillRatio:
        "Solo reparación. La corrección vertical rechaza leer alineaciones por debajo de esta ocupación.",
      preserveRange:
        "Solo reparación. Impide que la corrección vertical retire el último frente de un SKU.",
      onlyIfBetter:
        "Solo reparación. Conserva la corrección vertical únicamente si mejora la puntuación estética compuesta.",
      brandAttribute:
        "Es el mismo ajuste que Rol de marca. Agrupa columnas verticales y comprueba marcas eliminadas.",
      tierAttribute:
        "Es el mismo ajuste que Rol de nivel. Los valores se evalúan con el orden configurado; sin orden, la medida se desactiva.",
      sizeAttribute:
        "Es el mismo ajuste que Rol de cantidad. Alimenta el orden de tamaños y el peso de volumen derivado.",
      flavourAttribute:
        "Es el mismo ajuste que Rol de variante terminal. Alimenta las comprobaciones de variante y valor final.",
      verticalAttributes:
        "Es el mismo ajuste que Orden de roles verticales. Define la ruta agrupada como bloque vertical.",
      tierLabel:
        "Nombre visible del nivel. La posición de la fila, no el texto, define el orden de arriba abajo.",
      tierValues:
        "Valores del nivel sin distinguir mayúsculas. Los valores no listados no se evalúan como nivel.",
      lastInBlock:
        "Puntuación de orden. Los valores o nombres con estos términos deben cerrar su bloque; una lista vacía no aplica reglas implícitas.",
      bayOwners:
        "Puntuación de bloqueo. Los bloques coincidentes también deben alinear sus bordes con módulos completos.",
      bulkPounds:
        "Puntuación ergonómica. Peso derivado mínimo considerado voluminoso; 0 desactiva esta evaluación.",
      lowerShelfShare:
        "Puntuación ergonómica. Fracción de estantes inferiores considerada zona de elevación para productos pesados.",
      jitterPacks:
        "Puntuación de bloqueo. Anchuras de paquete perdonadas antes de penalizar bordes verticales.",
      raggedZeroPacks:
        "Puntuación de bloqueo. Irregularidad excedente, en paquetes, donde un bloque vertical llega a cero.",
      flagsPerHundredZero:
        "Detector CDT. Alertas ponderadas por 100 SKUs donde la puntuación del detector llega a cero.",
      blockingPenalty: "Penalización del detector para cada alerta de gravedad bloqueante.",
      toCheckPenalty: "Penalización del detector para cada alerta por comprobar.",
      toReviewPenalty: "Penalización del detector para cada alerta por revisar.",
      blockingWeight: "Peso del bloqueo en la puntuación total. Los cuatro pesos deben sumar 100.",
      cdtWeight:
        "Peso del cumplimiento CDT en la puntuación total. Los cuatro pesos deben sumar 100.",
      fullnessWeight:
        "Peso de huecos y salientes en la puntuación total. Los cuatro pesos deben sumar 100.",
      ergonomicsWeight:
        "Peso de la ubicación de productos pesados. Los cuatro pesos deben sumar 100.",
      verticalSplit: "Parte del bloqueo asignada al vertical; vertical y horizontal deben sumar 1.",
      horizontalSplit:
        "Parte del bloqueo asignada a niveles horizontales; vertical y horizontal deben sumar 1.",
      detectorSplit:
        "Parte del CDT asignada a alertas del detector; detector y orden deben sumar 1.",
      orderingSplit: "Parte del CDT asignada al orden; detector y orden deben sumar 1.",
      fullProfileJson:
        "Perfil completo validado, incluidas reglas y asignaciones exclusivas del JSON. Los campos sencillos reescriben este texto.",
    },
    advancedReference: {
      title: "Propiedades exclusivas del JSON",
      sectionSemantics:
        "Remapeo por sección, condiciones y niveles omitidos. Gana la primera asignación coincidente.",
      overlayAttr: "Atributo activo representado por un nivel del perfil.",
      unitAliases:
        "Grafías de unidades, sin distinguir mayúsculas, asignadas a unidades canónicas.",
      reconciliationBlockAttributes:
        "Atributos que definen grupos de evidencia de cantidad; false desactiva la conciliación.",
      firstValueRules:
        "Subcadenas por atributo preferidas al inicio del orden de valores hermanos.",
      lastValueRules:
        "Subcadenas por atributo preferidas al final; la regla terminal también alimenta la puntuación de cierre.",
    },
    help: {
      attributes:
        "Este es el orden de preferencia de candidatos guardado; la geometría del estante aún decide la jerarquía.",
      commaSeparated: "Separa los valores con comas; se conservará su orden.",
      advanced:
        "Aquí están disponibles todos los valores del perfil, incluidas las reglas de valores y los alias de unidades. Un JSON de perfil válido actualiza los campos sencillos; al cargar siempre se valida y compila el perfil completo.",
    },
    errors: {
      saveFailed: "No se pudo guardar la configuración. Inténtalo de nuevo.",
      title: "Corrige estos problemas antes de cargar",
      invalidJson: "El editor de perfil completo no contiene un JSON válido.",
      persistence:
        "No se pudo guardar la configuración en este navegador. Comprueba el almacenamiento disponible.",
    },
    saved: "Configuración guardada para esta categoría.",
  },
  configuration: {
    settingsLoadFailed: "No se pudo cargar la configuración del comercio. Inténtalo de nuevo.",
    accessDenied: "No tienes permiso para editar la configuración de CDT.",
    archivedCategory: "Las categorías archivadas no pueden activar la configuración de CDT Studio.",
    invalidCategory: "Categoría no válida.",
    loadFailed: "No se pudo cargar esta categoría.",
    loading: "Cargando la configuración de CDT Studio…",
  },
  severity: {
    blocking: "Bloqueante",
    toCheck: "Por comprobar",
    toReview: "Por revisar",
    resolved: "Resuelto",
  },
  toolbar: {
    undo: "Deshacer",
    redo: "Rehacer",
    detect: "Detectar excepciones de merchandising",
    fixBlocking: "Corregir bloqueo vertical",
    finalize: "Finalizar CDTs",
    downloadCsv: "Guardar CDTs en archivo",
  },
  provenance: {
    noNewBreaks: "Sin nuevas divisiones — tus cambios no rompieron nada.",
    regenDelta:
      "La regeneración reproduce {{exact}}/{{total}} productos en su estante exacto — las excepciones de esta vista previa pueden ser artefactos de la regeneración, no tus cambios",
    regenShed: "el plan regenerado lleva {{count}} frente(s) cedido(s)",
    regenOverflow:
      "{{lanes}} estante(s) llevan {{cm}}cm más que su lineal — los productos se comprimen y pueden solaparse",
    newBreaks: "{{count}} bloques rotos por tus cambios",
    moved: "{{count}} SKUs movidos vs base",
  },
  evidence: {
    built: "Construido: {{order}}",
    want: "Esperado: {{order}}",
    fix: "Corregir",
    acceptNamesVariant: 'Las excepciones aceptadas generan una regla que nombra "{{variant}}".',
    acceptNoRow:
      "Este ámbito no se resuelve a un único valor CDT0 publicado, así que aceptar registra la excepción pero no escribe ninguna regla.",
    accept: "Aceptar como excepción",
    dismiss: "Descartar",
  },
  resolver: {
    blockingTooSparse:
      "Bloqueo vertical no aplicado: las estanterías están llenas solo al {{fill}}%, y la corrección lee sus bandas del plan tal como está. Primero llena el plan (o regenéralo) y luego cuadra los bloques.",
    blockingFixed:
      "Bloqueo vertical: estética {{scoreBefore}} → {{scoreAfter}}. {{added}} frente(s) añadidos, {{removed}} retirados en {{skus}} SKU(s); {{unranged}} SKU(s) fuera del plan; {{ragged}}cm de borde irregular restante; {{residuals}} bloque(s) aún fuera de su banda.",
    blockingNoGain:
      "Bloqueo vertical: sin cambios. Cuadrar este plan obtuvo {{scoreAfter}} frente al {{scoreBefore}} actual, así que el plan resultante no sería mejor que el que ya tienes. No se movió nada ni se retiró ningún SKU.",
    cannotRepairInPlace: "No se puede reparar en el sitio",
    cannotRepairInPlaceDetail:
      "El resolutor no pudo mover nada dentro de este bloque, así que el plano no ha cambiado. Regenerar todo el diseño es una decisión aparte — usa el interruptor de vista previa.",
    autoResolve: "Resolver automáticamente",
    partial: "Parcial",
    refused: "No resuelto",
    dataOnly: "Cambio de datos / árbol",
    resetFixes: "Restablecer arreglos",
    repositioned: "{{count}} frentes reubicados respecto a la base",
    regenerated:
      "Plan regenerado desde el árbol — cada bloque sigue el orden del árbol; las excepciones restantes se miden sobre el layout regenerado",
    auditStrip:
      "pares invertidos {{inverted}} · cortes de carril {{laneBreaks}} · bloques divididos {{splits}}",
    gradeTitle: "Gradiente de tamaño · todos los bloques",
    gradeSkipped: "{{node}} — {{why}}",
    gradePinned: "{{node}}: {{id}} retenido arriba — demasiado alto para el estante inferior",
    violation: "Violación",
    needsReview: "Requiere revisión",
    suggested: "Resolución sugerida:",
    suggestion: {
      relocate:
        "Resolver automáticamente — devolver los sueltos a su bloque, comprando espacio con frentes.",
      resequence: "Resolver automáticamente — reordenar el bloque según la secuencia del árbol.",
      retag: "Cambio de datos / árbol — reetiquetar los SKUs; nada se mueve en el estante.",
    },
  },
  inference: {
    conventionChip: "Convención ({{pct}})",
    exceptionsTitle: "Excepciones de inferencia ({{count}})",
    exceptionChip: "Excepción",
    alertChip: "Alerta",
    kind: {
      "orient-disagrees": "orientación",
      "no-order": "sin orden inferible",
      "contrary-block": "contra la corriente",
      "order-drift": "deriva de orden",
      "gradient-section": "gradiente de sección",
      "size-unparsed": "tamaño imputado",
      "unit-mix": "familias de unidades mezcladas",
      "pin-backwards": "el defecto reclama un orden inverso",
    },
  },
  panel: {
    closeDirtyTitle: "¿Cerrar el estudio y perder tu trabajo?",
    closeDirtyDescription:
      "Los descartes, excepciones aceptadas, correcciones aplicadas y ediciones del árbol aún no se han guardado. Al cerrar se pierden.",
    closeKeepEditing: "Seguir editando",
    closeDiscard: "Descartar y cerrar",
    close: "Cerrar",
    location: "Módulo {{bay}} · Estante {{shelf}}",
    previewRegenerated: "Previsualizar el layout regenerado",
    inferredFromBasePog: "CDT inferido del planograma base",
    findings: "Excepciones ({{count}})",
    loadingAttributes: "Cargando el maestro de artículos...",
    attributesFailed: "No se pudieron cargar los atributos del maestro desde la base de datos.",
    shipping: "Guardando los CDT...",
    shipped:
      "Se guardaron {{count}} productos en la tabla CDT — árbol {{treeId}}, versión {{releaseId}}",
    shipNoMerchflow:
      "No hay un merchflow en contexto, así que no existe un árbol de decisión donde guardar. Abre el estudio desde el planograma de un merchflow.",
    storeRulesRequired:
      "No se puede guardar este CDT porque sus medidas por tienda no coinciden con el árbol mostrado en CDT Studio. Corrige el árbol e inténtalo de nuevo.",
    shippedWithGaps:
      "Se guardaron {{count}} productos en la tabla CDT — árbol {{treeId}}, versión {{releaseId}}. {{dropped}} producto(s) se guardaron sin ubicación y no se generarán.",
    orderChanged:
      "Conviene revisarlo: el CDT guardado no puede mantener todos los productos en el orden que ves aquí, así que los planogramas generados colocarán algunos en otra secuencia.",
    staleRules:
      "{{count}} regla(s) de ubicación no se guardaron: {{rules}} ya no es una sección de este árbol, así que la regla vaciaría cada estante que nombra.",
    shippedWithoutRules:
      "El CDT está guardado, pero las reglas de ubicación no — nada sujeta esos productos a sus estantes cuando se generen los planogramas.",
    unplacedTitle: "{{count}} producto(s) tienen CDT pero ningún lugar en este planograma:",
    unplacedCombo: "{{cdt0}} / {{cdt1}}",
    unplacedProduct: "· {{name}} ({{productCode}})",
    unplacedConsequence:
      "Si continúas se guarda el CDT, pero el generador no colocará estos {{count}} producto(s) en ningún planograma.",
    unplacedContinue: "Finalizar de todos modos",
    gateTitle: "¿Guardar las reglas de ubicación para los planogramas de tienda?",
    gateOwnsShelf: "{{variant}} se queda en el estante {{shelves}}",
    gateOwnsShelves: "{{variant}} se queda en los estantes {{shelves}}",
    gateNoCost: "No hay que mover nada más: ningún otro producto sale de un estante.",
    gateEvicts:
      "{{evictions}} colocación(es) existente(s) en {{siblings}} grupo(s) de productos más tendrían " +
      "que moverse.",
    gateCarried: "{{count}} grupo(s) existente(s) se conservan sin cambios",
    gateOk: "Guardar reglas de ubicación",
    gateCancel: "Cancelar",
    gateRefusalNoRows: "No hay filas de restricción para guardar.",
    gateRefusalNoCategory:
      "Este merchflow no tiene id de categoría, así que no se pueden usar los endpoints de restricciones.",
    gateRefusalCannotRead:
      "No se pudieron cargar las restricciones existentes. Guardar reemplazaría toda la categoría.",
    gateRefusalEmptyStoreScope:
      "Algunos grupos de restricciones no tienen códigos de tienda. Reasígnalos o elimínalos antes de guardar.",
    gateRefusalGeneric: "{{detail}}",
    gateSaveFailed: "Falló el guardado de restricciones: {{message}}",
  },
  moveReport: {
    budgetTitle: "Este movimiento cuesta frentes",
    budgetDescription:
      "Quita {{facings}} frentes ({{pct}}% del plano) y deja unos {{metres}} m de estante vacío. El árbol quedará bien; el estante quedará más vacío.",
    budgetFillTitle: "Este movimiento cuesta llenado",
    budgetFillDescription:
      "Mantiene los frentes pero cuesta {{pts}} puntos de llenado ({{before}}% → {{after}}%), dejando unos {{metres}} m de estante sin cubrir. El árbol quedará bien; el estante quedará más vacío.",
    budgetCancel: "No mover",
    budgetProceed: "Mover igualmente",
    surgical: "Movimiento ejecutado quirúrgicamente — se preserva todo lo que no se pidió cambiar",
    replay: "Movimiento ejecutado por regeneración",
    mechanismSlide: "Bloque reordenado — el plan no se volvió a derivar desde la jerarquía",
    mechanismReband: "Niveles reapilados — el plan no se volvió a derivar desde la jerarquía",
    churn:
      "{{moved}} de {{total}} productos se recorrieron · {{facings}} con frentes redistribuidos",
    churnNone: "cada producto mantuvo su posición exacta",
    mechanismRemeasure:
      "Re-medido en su lugar — el plan sobrevivió al cambio de jerarquía; solo se volvió a leer el contrato",
    mechanismRebuild: "Reconstrucción — todo el plan se derivó de nuevo desde la jerarquía",
    mechanismBranchRebuild:
      "Rama recolocada según su nueva escalera — todo lo demás permanece en su lugar",
    noop: "Sin cambios — el movimiento ya estaba en su lugar",
    aesthetics: "Estética {{before}} → {{after}} (cada plan medido contra su propia jerarquía)",
    fill: "Ocupación {{before}}% → {{after}}%",
    fillWorstRun: "· peor pasillo {{run}}: {{before}}% → {{after}}%",
    fillOverflow: "· desborde {{cm}} cm (contado aparte de la ocupación)",
    expectedSales: "Ventas esperadas sin cambio — no se agregó ni quitó producto",
    expectedSalesDelta: "Ventas esperadas {{before}} → {{after}}",
  },
  hierarchy: {
    blockedVertical: "vertical",
    blockedHorizontal: "horizontal",
    moveLevelUp: "Subir {{label}} un nivel",
    moveLevelDown: "Bajar {{label}} un nivel",
    poolingAmbiguous: "bloqueo ambiguo con {{ratio}} frentes por estante — toca para decidir",
    ambiguousLead: "Bloqueo ambiguo — el estudio adivinó, tú decides (las respuestas persisten):",
    nodeMeta: "{{count}} SKUs · {{flags}} excepciones",
    moveEarlier: "Mover {{label}} antes",
    moveLater: "Mover {{label}} después",
    moveEarlierShort: "◀ Antes",
    moveLaterShort: "Después ▶",
    showValues: "Listar los valores de {{label}} aquí",
    hideValues: "Ocultar los valores de {{label}}",
    showNextLevel: "Mostrar el nivel bajo {{label}}",
    hideNextLevel: "Ocultar el nivel bajo {{label}}",
    liftLevelHere: "Poner {{label}} sobre {{above}}, solo en esta rama",
    liftLevelShort: "↑",
    liftLevelAbove: "↑ Sobre {{above}}",
    liftLevelTitle: "Poner {{label}} sobre {{above}}, solo en esta rama",
    rungScope: "nivel, dentro de {{scope}}",
    rungDone: "Listo",
    wholePlan: "todo el plan",
    siblingPosition: "{{position}} de {{total}} hermanos",
    collapseNode: "Contraer",
    expandNode: "Expandir",
    selectHint: "Selecciona un nodo para reordenarlo entre sus hermanos.",
    expandBranch: "Expandir {{label}}",
    collapseBranch: "Contraer {{label}}",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    zoomFit: "Ajustar",
    zoomFitTitle: "Ajustar todo el árbol a la vista",
    zoomLevel: "{{percent}}%",
    expandAllNodes: "Expandir todos los nodos",
    collapseAllNodes: "Contraer todos los nodos",
    expandGraph: "Expandir gráfico",
    collapseGraph: "Contraer gráfico",
  },
};
